/* Contract detail page — shows contract info, modules, order forms, clauses, document versions,
   and provides edit/download actions with brand-aligned UI. Document generation happens
   automatically (async) on wizard completion: while a request is outstanding this page derives
   generationInFlight from the contract record (generationRequestedAt vs
   documentGeneratedAt/generationError) and mounts GenerationStatusBanner, which polls
   generation-status directly — this works after a refresh too, since it reads from the
   contract, not page state. The legacy ?generationFailed=true banner below covers the
   narrower case of the initial POST itself being rejected (auth/404/network), before a
   generationRequestedAt could even be stamped. A standing amber banner also reads
   contract.generationWarnings directly — present only when the latest COMPLETED run
   warned — so it survives a refresh; it is suppressed whenever the live banner is
   mounted (in flight or latched post-completion), since that banner's completed
   state shows the same warnings. Generated contracts get two edit
   entry points — the "Edit selections & regenerate" shortcut (resumes the wizard at the
   Clauses step) and the full "Edit Contract" (resumes at the Template step) — both routed
   through a single confirm modal parameterised by the target wizardStep. */
import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Download, Archive, Pencil } from "lucide-react";
import {
  useContract,
  useUpdateContract,
  useDownloadVersion,
  useTemplateWithSchema,
  useClauses,
} from "../hooks";
import { useOrgTemplate } from "../hooks/useOrgTemplates";
import {
  Card,
  Button,
  Badge,
  LoadingSpinner,
  Alert,
} from "../components/common";
import Modal from "../components/common/Modal";
import GenerationStatusBanner from "../components/common/GenerationStatusBanner";
import { partyRoleLabel } from "../utils/partyRoleLabel";

export default function ContractEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [generationFailed] = useState(
    searchParams.get("generationFailed") === "true",
  );
  // The wizard passes the generation failure reason as router state. The
  // banner's lifetime is governed by the generationFailed param (captured
  // above, then cleared from the URL on mount) — the reason only ever
  // renders inside that banner, so it disappears with it.
  const [generationError] = useState<string | null>(
    (location.state as { generationError?: string } | null)?.generationError ?? null,
  );
  // null = closed; otherwise the wizardStep the confirm modal will send on continue.
  const [editModalStep, setEditModalStep] = useState<
    "template" | "clauses" | null
  >(null);
  const [showAllVersions, setShowAllVersions] = useState(false);
  // Latch: true once a generation has been observed in flight during this
  // page-view. When the banner reaches 'completed' it invalidates the
  // contract detail; the refetched contract flips the in-flight derivation
  // off, so without the latch the parent would unmount the banner moments
  // after the success state (and its Download button) appeared. The banner's
  // own status query is cached and its polling has stopped, so it keeps
  // showing the terminal state. A fresh page load after completion is
  // quiescent (no latch, no banner), which is the intended behaviour.
  const [hasShownGenerationBanner, setHasShownGenerationBanner] =
    useState(false);
  // The search palette navigates contract -> contract on the same route
  // element (no unmount), so the latch above must be keyed by contract id —
  // otherwise a latch set while viewing an in-flight contract survives the
  // navigation and mounts a permanent, disabled-poll banner on an unrelated,
  // quiescent contract. Render-time prev-value guard (not an effect) so the
  // reset lands in the same render that first observes the new id.
  const [prevContractId, setPrevContractId] = useState(id);
  if (id !== prevContractId) {
    setPrevContractId(id);
    if (hasShownGenerationBanner) setHasShownGenerationBanner(false);
  }

  // Clear the query param on mount so refresh/bookmark doesn't re-show the banner
  useEffect(() => {
    if (searchParams.has("generationFailed")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("generationFailed");
          return next;
        },
        { replace: true },
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: contract, isLoading, error } = useContract(id!);
  const updateMutation = useUpdateContract();
  const downloadVersionMutation = useDownloadVersion();

  // Fetch global template schema (disabled for org-sourced contracts to avoid 404s)
  const isGlobalTemplate = contract?.templateSource !== "organisation";
  const isOrgTemplate = contract?.templateSource === "organisation";
  const { data: templateSchema } = useTemplateWithSchema(
    isGlobalTemplate ? contract?.templateId || "" : "",
  );
  const { data: clauseData } = useClauses(contract?.templateId || "");
  // Fetch org template for organisation-sourced contracts
  const { data: orgTemplate } = useOrgTemplate(
    isOrgTemplate ? contract?.templateId || "" : "",
  );

  // Lookup maps for human-readable names
  const moduleMap = useMemo(
    () => new Map(templateSchema?.schema?.modules?.map((m) => [m.id, m]) || []),
    [templateSchema],
  );

  const orderFormMap = useMemo(
    () =>
      new Map(
        templateSchema?.schema?.modules?.flatMap((m) =>
          m.orderForms.map((of) => [`${m.id}:${of.id}`, of]),
        ) || [],
      ),
    [templateSchema],
  );

  const clauseMap = useMemo(
    () => new Map(clauseData?.items?.map((c) => [c.id, c]) || []),
    [clauseData],
  );

  // Check if template has been updated since document generation
  // Must be before early returns to maintain hook order
  const isGenerated = contract?.status === "generated";
  const isTemplateOutdated = useMemo(() => {
    if (!contract?.templateVersionRef || !isGenerated) return false;

    if (contract.templateSource === "organisation") {
      if (!orgTemplate) return false;
      return (
        String(orgTemplate.currentVersion) !==
        String(contract.templateVersionRef)
      );
    } else {
      if (!templateSchema) return false;
      return (
        String(templateSchema.version) !== String(contract.templateVersionRef)
      );
    }
  }, [
    contract?.templateVersionRef,
    contract?.templateSource,
    isGenerated,
    templateSchema,
    orgTemplate,
  ]);

  const handleArchive = async () => {
    if (!id || !confirm("Are you sure you want to archive this contract?"))
      return;
    await updateMutation.mutateAsync({ id, input: { status: "archived" } });
    navigate("/generate/contracts");
  };

  const handleEdit = () => {
    if (!id || !contract) return;
    if (contract.status === "generated") {
      setEditModalStep("template");
    } else {
      navigate(`/generate/contracts/new?resume=${id}`);
    }
  };

  // Shortcut for generated contracts: reopens the wizard at the Clauses step
  // rather than the beginning, since details/modules/schedules are unlikely
  // to need revisiting when only clause selections changed.
  const handleEditClauses = () => {
    if (!id || !contract || contract.status !== "generated") return;
    setEditModalStep("clauses");
  };

  const handleConfirmEdit = async () => {
    if (!id || !editModalStep) return;
    const wizardStep = editModalStep;
    setEditModalStep(null);
    await updateMutation.mutateAsync({
      id,
      input: { status: "draft", wizardStep },
    });
    // Set fresh draft data directly in cache so the wizard doesn't see stale 'generated' status
    queryClient.setQueryData(["contracts", id], (old: unknown) => {
      if (old && typeof old === "object") {
        return { ...old, status: "draft", wizardStep };
      }
      return old;
    });
    navigate(`/generate/contracts/new?resume=${id}`);
  };

  const handleDownload = async (version: number) => {
    if (!id) return;
    try {
      const result = await downloadVersionMutation.mutateAsync({
        contractId: id,
        version,
      });
      if (result.documentUrl) {
        window.open(result.documentUrl, "_blank");
      }
    } catch {
      // Download errors are non-critical; swallow
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 dark:text-red-400">Contract not found</p>
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => navigate("/generate/contracts")}
        >
          Back to Contracts
        </Button>
      </div>
    );
  }

  const isDraftIncomplete =
    contract.status === "draft" && contract.wizardStep !== "complete";
  const isDraftComplete =
    contract.status === "draft" && contract.wizardStep === "complete";

  // A generation request is still outstanding when one has been requested,
  // the document generated since then hasn't caught up to it, and the most
  // recent attempt hasn't already recorded an error. This is a pure
  // derivation from the contract record (not page state), so refreshing
  // mid-generation still shows the live status banner.
  const generationInFlight =
    !!contract.generationRequestedAt &&
    !(
      contract.documentGeneratedAt &&
      contract.documentGeneratedAt >= contract.generationRequestedAt
    ) &&
    !contract.generationError;

  // Render-time prev-value guard (not an effect) so the latch is set in the
  // same render that first observes the in-flight state.
  if (generationInFlight && !hasShownGenerationBanner) {
    setHasShownGenerationBanner(true);
  }

  // Warnings recorded against the latest completed generation run — the
  // standing banner's data source (from the contract record, not the poll).
  const standingWarnings = contract.generationWarnings;

  const versions = contract.documentVersions || [];
  const displayVersions = showAllVersions ? versions : versions.slice(-5);
  const sortedVersions = [...displayVersions].sort(
    (a, b) => b.version - a.version,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            iconOnly
            size="md"
            icon={<ArrowLeft className="h-5 w-5" />}
            aria-label="Go back"
            onClick={() => navigate("/generate/contracts")}
          />
          <div>
            <h1 className="text-h1 text-slate-900 dark:text-slate-50">
              {contract.title}
            </h1>
            <div className="flex items-center space-x-2 mt-1">
              <Badge status={contract.status} />
              {contract.documentVersion > 0 && (
                <span className="text-body-sm text-brand-600 dark:text-brand-400 font-medium">
                  Version {contract.documentVersion}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {isDraftIncomplete && (
            <Button
              icon={<ArrowRight className="h-4 w-4" />}
              onClick={() => navigate(`/generate/contracts/new?resume=${id}`)}
            >
              Continue Editing
            </Button>
          )}
          {isDraftComplete && (
            <Button
              variant="secondary"
              icon={<Pencil className="h-4 w-4" />}
              onClick={handleEdit}
            >
              Edit Contract
            </Button>
          )}
          {isGenerated && (
            <>
              <Button
                icon={<Pencil className="h-4 w-4" />}
                onClick={handleEditClauses}
              >
                Edit selections &amp; regenerate
              </Button>
              <Button
                variant="secondary"
                icon={<Pencil className="h-4 w-4" />}
                onClick={handleEdit}
              >
                Edit Contract
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Edit Confirmation Modal */}
      <Modal
        isOpen={editModalStep !== null}
        onClose={() => setEditModalStep(null)}
        title={
          editModalStep === "clauses" ? "Edit selections & regenerate" : "Edit Contract"
        }
      >
        <p className="text-body-sm text-slate-600 dark:text-slate-400 mb-6">
          {editModalStep === "clauses" ? (
            <>
              This reopens the wizard at the Clauses step. Your contract
              details, modules and schedules stay as they are, and previous
              document versions are kept. The contract returns to draft until
              you complete the wizard.
            </>
          ) : (
            <>
              This will set the contract back to draft for editing. A new
              document will be generated automatically when you complete the
              wizard.
            </>
          )}
        </p>
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={() => setEditModalStep(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmEdit}
            loading={updateMutation.isPending}
          >
            Continue
          </Button>
        </div>
      </Modal>

      {/* Live async generation status banner — polls generation-status while a
          request is outstanding, and stays mounted (via the latch) so its
          terminal success/failure state remains visible after the refetched
          contract flips the in-flight derivation off. */}
      {(generationInFlight || hasShownGenerationBanner) && (
        <GenerationStatusBanner contractId={id!} inFlight={generationInFlight} />
      )}

      {/* Generation Failed Banner (from wizard auto-generation failure) */}
      {generationFailed &&
        isDraftComplete &&
        contract.documentVersion === 0 && (
          <Alert variant="warning">
            <p className="font-medium">Document generation failed</p>
            <p className="mt-1">
              The document could not be generated automatically.
            </p>
            {generationError && (
              <p className="mt-1 font-mono text-body-sm">{generationError}</p>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={handleEdit}
            >
              Edit contract to try again
            </Button>
          </Alert>
        )}

      {/* Standing generation-warnings banner — reads the contract record
          directly (generationWarnings), not the live poll, so it shows on a
          fresh page load / after a refresh. Suppressed whenever the live
          GenerationStatusBanner is mounted (in flight OR latched after an
          in-session completion): its completed state renders the same
          warnings inset, so showing both would duplicate the list. */}
      {!(generationInFlight || hasShownGenerationBanner) &&
        standingWarnings &&
        standingWarnings.length > 0 && (
          <Alert variant="warning">
            <p className="font-medium">
              The latest document was generated with warnings
            </p>
            <ul className="mt-1 space-y-1">
              {standingWarnings.map((warning, index) => (
                <li key={index} className="font-mono text-body-sm">
                  {warning}
                </li>
              ))}
            </ul>
            <p className="mt-2">
              Review the affected clauses in Clause Admin or Template Health to
              confirm the generated wording is correct.
            </p>
          </Alert>
        )}

      {/* Template Updated Warning */}
      {isTemplateOutdated && (
        <Alert variant="warning">
          <p className="font-medium">Template updated</p>
          <p className="mt-1">
            The template has been updated since this document was generated.
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={handleEdit}
          >
            Edit contract to generate new version
          </Button>
        </Alert>
      )}

      {/* Cross-reference refresh note */}
      {versions.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-brand-200 bg-brand-50 p-4 text-body-sm text-brand-800 dark:border-brand-800 dark:bg-brand-900/20 dark:text-brand-200"
        >
          After downloading the generated document, open it in Word, press{" "}
          <kbd className="bg-brand-100 text-brand-800 font-mono text-caption rounded px-1.5 py-0.5">
            Ctrl+A
          </kbd>{" "}
          to select all text, then{" "}
          <kbd className="bg-brand-100 text-brand-800 font-mono text-caption rounded px-1.5 py-0.5">
            F9
          </kbd>{" "}
          to update cross-reference data.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contract Details */}
          <Card>
            <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
              Contract Details
            </h2>
            {contract.description && (
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {contract.description}
              </p>
            )}
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-body-sm font-medium text-slate-500 dark:text-slate-400">
                  Reference Number
                </dt>
                <dd className="text-body-sm text-slate-900 dark:text-slate-50">
                  {contract.referenceNumber || "Not set"}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Parties */}
          <Card>
            <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
              Parties
            </h2>
            <div className="space-y-4">
              {contract.parties.map((party, index) => (
                <div
                  key={index}
                  className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900 dark:text-slate-50">
                      {party.name}
                    </span>
                    <span className="text-body-sm text-slate-500 dark:text-slate-400">
                      {partyRoleLabel(party.role)}
                    </span>
                  </div>
                  {party.abn && (
                    <p className="text-body-sm text-slate-600 dark:text-slate-400">
                      ABN: {party.abn}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Modules with Order Forms */}
          <Card>
            <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
              Selected Modules
            </h2>
            <div className="space-y-3">
              {contract.modules
                .filter((m) => m.enabled)
                .map((module) => (
                  <div
                    key={module.moduleId}
                    className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                        {moduleMap.get(module.moduleId)?.name ||
                          module.moduleId}
                      </span>
                    </div>
                    {module.selectedOrderForms.length > 0 && (
                      <div className="mt-2 ml-3 pl-3 border-l-2 border-brand-200 dark:border-brand-700 space-y-1">
                        <span className="text-overline uppercase text-slate-500 dark:text-slate-400">
                          Order Forms
                        </span>
                        {module.selectedOrderForms.map((ofId) => (
                          <div
                            key={ofId}
                            className="text-body-sm text-slate-700 dark:text-slate-300"
                          >
                            {orderFormMap.get(`${module.moduleId}:${ofId}`)
                              ?.name || ofId}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </Card>

          {/* Schedules */}
          {contract.schedules &&
            contract.schedules.filter((s) => s.enabled).length > 0 && (
              <Card>
                <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
                  Selected Schedules
                </h2>
                <div className="space-y-3">
                  {contract.schedules
                    .filter((s) => s.enabled)
                    .map((schedule) => (
                      <div
                        key={schedule.scheduleId}
                        className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                          {schedule.scheduleId}
                        </span>
                      </div>
                    ))}
                </div>
              </Card>
            )}

          {/* Clause Selections */}
          {contract.clauseSelections &&
            contract.clauseSelections.length > 0 && (
              <Card>
                <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
                  Selected Clauses
                </h2>
                <div className="space-y-2">
                  {contract.clauseSelections.map((selection) => {
                    const clause = clauseMap.get(selection.clauseId);
                    const option = clause?.options?.find(
                      (o) => o.id === selection.optionId,
                    );

                    return (
                      <div
                        key={selection.clauseId}
                        className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center justify-between"
                      >
                        <div>
                          <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                            {clause?.name || selection.clauseId}
                          </span>
                          {clause?.referenceNumber && (
                            <span className="text-caption text-slate-500 dark:text-slate-400 ml-2">
                              {clause.referenceNumber}
                            </span>
                          )}
                        </div>
                        <span className="text-body-sm text-brand-600 dark:text-brand-400">
                          {option?.label || selection.optionId}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Document Versions */}
          {versions.length > 0 && (
            <Card>
              <h3 className="text-body-sm font-semibold text-slate-900 dark:text-slate-50 mb-4">
                Document Versions
              </h3>
              <div className="space-y-2">
                {sortedVersions.map((entry) => (
                  <div
                    key={entry.version}
                    className={`flex items-center justify-between p-2 rounded-md ${
                      entry.version === contract.documentVersion
                        ? "bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800"
                        : "bg-slate-50 dark:bg-slate-800"
                    }`}
                  >
                    <div>
                      <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                        v{entry.version}
                      </span>
                      <span className="text-caption text-slate-500 dark:text-slate-400 ml-2">
                        {new Date(entry.generatedAt).toLocaleDateString(
                          "en-AU",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          },
                        )}
                        {", "}
                        {new Date(entry.generatedAt).toLocaleTimeString(
                          "en-AU",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(entry.version)}
                      className="text-brand-600 hover:bg-brand-50 p-1.5 rounded-md focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={`Download version ${entry.version}`}
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              {versions.length > 5 && !showAllVersions && (
                <button
                  onClick={() => setShowAllVersions(true)}
                  className="text-body-sm text-brand-600 dark:text-brand-400 hover:text-brand-500 mt-2 focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 rounded-md px-1"
                >
                  Show all {versions.length} versions
                </button>
              )}
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <h3 className="text-body-sm font-semibold text-slate-900 dark:text-slate-50 mb-4">
              Information
            </h3>
            <dl className="space-y-3 text-body-sm">
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Created</dt>
                <dd className="text-slate-900 dark:text-slate-50">
                  {new Date(contract.createdAt).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500 dark:text-slate-400">
                  Last Updated
                </dt>
                <dd className="text-slate-900 dark:text-slate-50">
                  {new Date(contract.updatedAt).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Template</dt>
                <dd className="text-slate-900 dark:text-slate-50">
                  {contract.templateId}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Actions */}
          <Card>
            <h3 className="text-body-sm font-semibold text-slate-900 dark:text-slate-50 mb-4">
              Actions
            </h3>
            <Button
              variant="danger"
              icon={<Archive className="h-4 w-4" />}
              onClick={handleArchive}
              loading={updateMutation.isPending}
              className="w-full"
            >
              Archive Contract
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
